import React, { useCallback, useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
// Hooks
import { useHasMounted, useTicketingLaunched } from 'hooks/index';
// Api
import { DASHBOARD_STATS } from 'api/dashboard/queries';
// Types
import {
  GetDashboardStats,
  GetDashboardStatsVariables,
  GetDashboardStats_getDashboardStats,
  GetDashboardStats_getDashboardStats_popularItems,
} from 'api/dashboard/types/GetDashboardStats';
import { DashboardChartData } from './DashboardChart/DashboardChart';
// Helpers
import {
  formatCurrencyString,
  formatCryptoCurrencyString,
} from 'helpers/utils';
// Constants
import { isoDateFormat } from 'constants/global';
// Ui
import Table from 'ui/Table';
// Components
import DashboardHeader from './DashboardHeader/DashboardHeader';
import DashboardSection from './DashboardSection/DashboardSection';
import DashboardChart from './DashboardChart/DashboardChart';
import DashboardPromoCodes from './DashboardPromoCodes/DashboardPromoCodes';
import DashboardTicketSales from './DashboardTicketSales/DashboardTicketSales';
import { TicketStats } from './DashboardTicketSales/helpers';
// Styles
import styles from './Dashboard.module.scss';

type StatsDataItem = {
  date: string;
  total?: number;
  sum?: number;
};

type StatsDataJson = {
  ama_request: StatsDataItem[];
  experience_request: StatsDataItem[];
  memorabilia_order: StatsDataItem[];
  merch_order: StatsDataItem[];
  product_order: StatsDataItem[];
  stream_order: StatsDataItem[];
  stream_tip: StatsDataItem[];
  stream_tip_crypto: StatsDataItem[];
  user_tip: StatsDataItem[];
  user_tip_crypto: StatsDataItem[];
  offer: StatsDataItem[];
  event_marketing: StatsDataItem[];
  ticket_order?: StatsDataItem[];
};

type GraphItemData = {
  ama: number;
  experience: number;
  memorabilia: number;
  merch: number;
  products: number;
  streams: number;
  offers: number;
  eventMarketing: number;
  tips: number;
  cryptoTips: number;
  date: string;
};

export enum ChartType {
  Revenue = 'revenueStats',
  ProfitMargin = 'profitStats',
}

const Dashboard: React.FC = () => {
  const hasMounted = useHasMounted();
  const { launched: ticketingLaunched } = useTicketingLaunched();

  const { storeId } = useParams<{ storeId: string | undefined }>();

  const defaultStartDate = moment()
    .subtract(1, 'months')
    .startOf('day')
    .utc(true);
  const defaultEndDate = moment().endOf('day').utc(true);
  const [startDate, setStartDate] = useState<Moment>(defaultStartDate);
  const [currentChartType, changeChartType] = useState<ChartType>(
    ChartType.Revenue
  );
  const [endDate, setEndDate] = useState<Moment>(defaultEndDate);
  const [revenueChartData, setRevenueChartData] = useState<
    DashboardChartData[]
  >([]);
  const [profitChartData, setProfitChartData] = useState<DashboardChartData[]>(
    []
  );
  const [amountChartData, setAmountChartData] = useState<DashboardChartData[]>(
    []
  );

  const skip = Boolean(!startDate || !endDate);

  const { data } = useQuery<GetDashboardStats, GetDashboardStatsVariables>(
    DASHBOARD_STATS,
    {
      variables: {
        input: {
          from: startDate,
          to: endDate,
          storeId,
        },
      },
      // "skip ? 'cache-only' : 'cache-and-network'" is the hotfix for issue
      // that request has been sent even if "skip" is "true"
      // https://github.com/apollographql/apollo-client/issues/6190
      fetchPolicy: skip ? 'cache-only' : 'cache-and-network',
      skip,
    }
  );

  const dashboardStats: GetDashboardStats_getDashboardStats | undefined =
    data?.getDashboardStats;

  const totalRevenue: string = formatCurrencyString(
    dashboardStats?.revenueTotal || 0
  );

  const totalProfit: string = formatCurrencyString(
    dashboardStats?.profitTotal || 0
  );

  const cryptoProfit = dashboardStats?.cryptoProfitTotal || 0;
  const cryptoProfitFormatted = formatCryptoCurrencyString(cryptoProfit);

  const combinedProfit = `${totalProfit}  ${cryptoProfitFormatted}`;

  const totalAmount: string = formatCurrencyString(
    dashboardStats?.amountTotal || 0,
    '',
    0
  );

  const mostPopularData: GetDashboardStats_getDashboardStats_popularItems[] =
    dashboardStats?.popularItems
      ?.slice()
      ?.sort((a, b) =>
        a?.revenue && b?.revenue
          ? Number(a.revenue) < Number(b.revenue)
            ? 1
            : Number(a.revenue) > Number(b.revenue)
            ? -1
            : 0
          : 0
      ) || [];

  const onDateRangeChange = (start: Moment, end: Moment) => {
    setStartDate(start);
    setEndDate(end);
  };

  const setAllData = (startDate: Moment, endDate: Moment): void => {
    const getUniqueDates = (stats: StatsDataJson): string[] => {
      const dates = new Set<string>();
      const toDate = (date: any) => new Date(date);

      if (stats) {
        stats?.ama_request?.forEach((i: StatsDataItem) => dates.add(i.date));
        stats?.experience_request?.forEach((i: StatsDataItem) =>
          dates.add(i.date)
        );
        stats?.memorabilia_order?.forEach((i: StatsDataItem) =>
          dates.add(i.date)
        );
        stats?.merch_order?.forEach((i: StatsDataItem) => dates.add(i.date));
        stats?.product_order?.forEach((i: StatsDataItem) => dates.add(i.date));
        stats?.stream_order?.forEach((i: StatsDataItem) => dates.add(i.date));
        stats?.stream_tip?.forEach((i: StatsDataItem) => dates.add(i.date));
        stats?.user_tip?.forEach((i: StatsDataItem) => dates.add(i.date));
        stats?.offer?.forEach((i: StatsDataItem) => dates.add(i.date));
        stats?.event_marketing?.forEach((i: StatsDataItem) =>
          dates.add(i.date)
        );
        dates.add(startDate.format(isoDateFormat));
        dates.add(endDate.format(isoDateFormat));
      }

      return Array.from(dates).sort(
        (a, b) =>
          toDate(a)[Symbol.toPrimitive]('number') -
          toDate(b)[Symbol.toPrimitive]('number')
      );
    };

    const getStatValue = (
      stats: StatsDataJson,
      field:
        | 'ama_request'
        | 'experience_request'
        | 'memorabilia_order'
        | 'merch_order'
        | 'product_order'
        | 'stream_order'
        | 'stream_tip'
        | 'stream_tip_crypto'
        | 'user_tip'
        | 'user_tip_crypto'
        | 'offer'
        | 'event_marketing',
      date: string
    ): number => {
      let value = 0;
      if (stats) {
        const statItem: any = stats?.[field]?.find((i: any) => i.date === date);
        value = statItem?.total || statItem?.sum || 0;
      }
      return value;
    };

    const getData = (
      field: 'amountStats' | 'revenueStats' | 'profitStats'
    ): DashboardChartData[] => {
      const res: GraphItemData[] = [];
      const stats = dashboardStats?.[field];

      if (stats) {
        const uniqDates = getUniqueDates(stats);

        uniqDates.forEach((date) => {
          res.push({
            date: date,
            experience: getStatValue(stats, 'experience_request', date),
            memorabilia: getStatValue(stats, 'memorabilia_order', date),
            merch: getStatValue(stats, 'merch_order', date),
            products: getStatValue(stats, 'product_order', date),
            streams: getStatValue(stats, 'stream_order', date),
            ama: getStatValue(stats, 'ama_request', date),
            tips:
              getStatValue(stats, 'stream_tip', date) +
              getStatValue(stats, 'user_tip', date),
            cryptoTips:
              getStatValue(stats, 'stream_tip_crypto', date) +
              getStatValue(stats, 'user_tip_crypto', date),
            offers: getStatValue(stats, 'offer', date),
            eventMarketing: getStatValue(stats, 'event_marketing', date),
          });
        });
      }

      return res?.map((i) => ({
        ...i,
        dayNumber: moment(i.date).format('D'),
      }));
    };

    if (startDate && endDate) {
      setRevenueChartData(getData('revenueStats'));
      setProfitChartData(getData('profitStats'));
      setAmountChartData(getData('amountStats'));
    }
  };

  const memoizedSetAllData = useCallback(setAllData, [dashboardStats]);

  useEffect(() => {
    memoizedSetAllData(startDate, endDate);
  }, [dashboardStats, startDate, endDate, memoizedSetAllData]);

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      width: 80,
      render: function TypeCell(type: string) {
        return <span className={styles.dashboardTableTypeCol}>{type}</span>;
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      width: 100,
    },
    {
      title: 'Amount',
      dataIndex: 'count',
      width: 40,
    },
    {
      title: 'Total revenue',
      dataIndex: 'revenue',
      width: 80,
      render: function RevenueCell(revenue: string) {
        return formatCurrencyString(revenue || 0, '$', 2);
      },
    },
  ];

  if (!hasMounted) {
    return null;
  }

  const isProfitChart = currentChartType === ChartType.ProfitMargin;
  const ticketValues: TicketStats = isProfitChart
    ? dashboardStats?.profitStats
    : dashboardStats?.revenueStats;
  const ticketTotal = (ticketValues?.ticket_order || []).reduce(
    (sum: number, item: StatsDataItem) => sum + (item.total || 0),
    0
  );
  // A store's margin on tickets is what it earns; the platform's is the service fee
  const ticketValueName = !isProfitChart
    ? 'Ticket revenue'
    : storeId
    ? 'Host earnings'
    : 'Service fees';

  return (
    <div className={styles.dashboardWrapper}>
      <DashboardHeader
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={onDateRangeChange}
        onChangeChartType={changeChartType}
        currentChartType={currentChartType}
      />

      <div className={styles.dashboardBody}>
        <DashboardSection
          title={isProfitChart ? 'Margin profit' : 'Total Revenue'}
          value={isProfitChart ? combinedProfit : totalRevenue}
        >
          <DashboardChart
            data={isProfitChart ? profitChartData : revenueChartData}
            currency="$"
            cryptoCurrency="₿"
            separateBtcAxis
          />
        </DashboardSection>

        <DashboardSection title="Total Amount" value={totalAmount}>
          <DashboardChart data={amountChartData} />
        </DashboardSection>

        {ticketingLaunched && (
          <DashboardSection
            title="Event Tickets"
            value={formatCurrencyString(ticketTotal)}
            valueLabel={ticketValueName}
          >
            <DashboardTicketSales
              values={ticketValues}
              counts={dashboardStats?.amountStats}
              startDate={startDate}
              endDate={endDate}
              valueName={ticketValueName}
            />
          </DashboardSection>
        )}

        <DashboardSection title="Most Popular">
          <Table<GetDashboardStats_getDashboardStats_popularItems>
            columns={columns}
            data={mostPopularData}
            scroll={{ x: 400 }}
            scrollToTopOnPagination={false}
            defaultPageSize={5}
            rowKey={(record) =>
              `${record.type}_${record.title}_${record.count}_${record.revenue}`
            }
          />
        </DashboardSection>

        <DashboardSection title="Promo Codes">
          <DashboardPromoCodes
            startDate={startDate}
            endDate={endDate}
            storeId={storeId}
          />
        </DashboardSection>
      </div>
    </div>
  );
};

export default Dashboard;
